import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../../config/firebase';
import { signOut } from 'firebase/auth';

const EmailVerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const email = (location.state as { email: string })?.email || '';

  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);

  // Redirect if no email provided
  useEffect(() => {
    if (!email) {
      navigate('/');
    }
  }, [email, navigate]);

  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  const handleResendEmail = async () => {
    setResending(true);
    setResendError('');
    setResendSuccess(false);

    setResendError(
      'Resend is currently unavailable. Please try logging in - if your email is verified, you can access your account.'
    );
    setResending(false);

    // Note: Resending requires the user to be logged in
    // Since we sign out after account creation, this feature is limited
    // Users should instead try to log in, which will show if email is verified
  };

  if (!email) {
    return null; // Will redirect
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Icon */}
        <div className="flex justify-center mb-8">
          <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center">
            <svg
              className="w-12 h-12 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Check Your Email
          </h2>

          <p className="text-gray-600 mb-2">
            We've sent a verification link to:
          </p>

          <p className="text-lg font-semibold text-blue-600 mb-6">{email}</p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-700">
              <strong>Next steps:</strong>
            </p>
            <ol className="text-sm text-left text-gray-700 mt-2 space-y-1 ml-4 list-decimal">
              <li>Check your email inbox (and spam folder)</li>
              <li>Click the verification link in the email</li>
              <li>After verification, return here and click "Go to Login"</li>
              <li>Sign in with your email and password</li>
            </ol>
          </div>

          {/* Success Message */}
          {resendSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-green-700">
                ✓ Verification email sent successfully!
              </p>
            </div>
          )}

          {/* Error Message */}
          {resendError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-600">{resendError}</p>
            </div>
          )}

          {/* Go to Login Button (Primary Action) */}
          <button
            onClick={async () => {
              // Ensure user is signed out
              try {
                await signOut(auth);
              } catch (err) {
                // Ignore errors, user might already be signed out
              }
              navigate('/login');
            }}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition mb-4"
          >
            Go to Login Page
          </button>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">
                Didn't receive the email?
              </span>
            </div>
          </div>

          {/* Resend Button (Secondary) */}
          <button
            onClick={handleResendEmail}
            disabled={!canResend || resending}
            className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {resending
              ? 'Processing...'
              : canResend
                ? 'Request New Verification Email'
                : `Wait ${countdown}s to resend`}
          </button>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            <strong>Can't find the email?</strong>
            <br />
            Check your spam folder, or try logging in - you may already be
            verified!
          </p>
        </div>

        {/* Back to Home Link */}
        <div className="mt-4 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-blue-600 hover:underline"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage;
